import React from "react";
import { SupervisorLayout } from "@/layouts/SupervisorLayout";
import { Sparkles } from "lucide-react";

/**
 * Shared placeholder page for Phase D — Live Monitor, Settings, Notifications,
 * Activity Log. Uses the Supervisor shell and Inter typography hierarchy.
 * No fake data, no fake charts — a clean "coming soon" surface.
 */
export function ComingSoonPage({
  overline,
  title,
  description,
  icon: Icon = Sparkles,
  bullets = [],
  testid,
}) {
  return (
    <SupervisorLayout>
      <div className="p-5 md:p-8 max-w-[1600px]">
        <div className="mb-6">
          <div className="text-[10px] tracking-[0.28em] font-semibold text-white/45 uppercase flex items-center gap-2">
            <Icon size={12} /> {overline}
          </div>
          <h1 className="mt-2 text-2xl md:text-3xl font-semibold text-white tracking-tight" data-testid={testid ? `${testid}-heading` : undefined}>{title}</h1>
          <p className="mt-1.5 text-white/50 text-sm max-w-2xl font-normal">{description}</p>
        </div>

        <div
          className="rounded-xl border p-10 md:p-14 flex flex-col items-center text-center gap-4 max-w-3xl"
          style={{ borderColor: "rgba(255,255,255,0.08)", background: "#0a0a0d" }}
          data-testid={testid}
        >
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center"
            style={{ background: "rgba(0,229,255,0.08)", border: "1px solid rgba(0,229,255,0.25)", color: "var(--accent)" }}
          >
            <Icon size={24} />
          </div>
          <div className="text-[10px] tracking-[0.28em] font-semibold uppercase" style={{ color: "var(--accent)" }}>
            Coming Soon
          </div>
          <div className="text-white text-lg md:text-xl font-semibold">{title} is on the roadmap</div>
          <p className="text-white/55 text-sm max-w-md font-normal leading-relaxed">
            This module is scoped and will land in an upcoming release. Nothing in the current live build is affected.
          </p>
          {bullets.length > 0 && (
            <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 text-left w-full max-w-lg">
              {bullets.map((b, i) => (
                <li key={i} className="text-[13px] text-white/70 font-normal flex items-start gap-2 rounded-md px-3 py-2"
                    style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)" }}>
                  <span className="mt-1 w-1 h-1 rounded-full shrink-0" style={{ background: "var(--accent)" }} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </SupervisorLayout>
  );
}

export function LiveMonitorPage() {
  return <ComingSoonPage
    overline="Build · Live"
    title="Live Monitor"
    description="Multi-station wall view. Watch every active build on one screen."
    testid="live-monitor-placeholder"
    bullets={[
      "Grid of every LIVE / PAUSED station",
      "Signed-URL video previews",
      "One-click drill-down into a station",
      "Alerts when a build stalls",
    ]}
  />;
}

export function SettingsPage() {
  return <ComingSoonPage
    overline="System · Settings"
    title="Settings"
    description="Store-wide preferences: branding, Cloudflare, WhatsApp provider, timezone."
    testid="settings-placeholder"
    bullets={[
      "Cloudflare Stream credentials",
      "WhatsApp Business API credentials",
      "Rate-limit &amp; retention policies",
      "Custom store branding",
    ]}
  />;
}

export function NotificationsPage() {
  return <ComingSoonPage
    overline="System · Alerts"
    title="Notifications"
    description="Route operational alerts to email, WhatsApp or Slack."
    testid="notifications-placeholder"
    bullets={[
      "Build stalled &gt; N minutes",
      "Station goes offline",
      "Customer submits a low rating",
      "Failed WhatsApp delivery retries",
    ]}
  />;
}

export function ActivityLogPage() {
  return <ComingSoonPage
    overline="System · Audit"
    title="Activity Log"
    description="Full audit trail — every admin action, every lifecycle event, searchable."
    testid="activity-log-placeholder"
    bullets={[
      "Every START / PAUSE / RESUME / FINISH",
      "Order creates &amp; deletes",
      "Technician profile changes",
      "Login / logout &amp; role changes",
    ]}
  />;
}
